import React from 'react';
import { Star, Clock, RefreshCw, AlertCircle, CheckCircle, ThumbsUp, Award, TrendingUp, Search } from 'lucide-react';
import { useGoogleReviews } from '../hooks/useGoogleReviews';
import { motion } from 'framer-motion';

interface GoogleReviewsBoxProps {
  businessName: string;
  location?: string;
  prospectId: string;
  domain: string;
  className?: string;
}

const GoogleReviewsBox: React.FC<GoogleReviewsBoxProps> = ({
  businessName,
  location,
  prospectId,
  domain,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  className = '' // Paramètre non utilisé mais gardé pour compatibilité API
}) => {
  const getSafeNumber = (value: number | string | { value: string | number } | null | undefined): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    if (typeof value === 'object' && value?.value) {
      const parsed = parseFloat(String(value.value));
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const {
    businessInfo,
    error,
    taskStatus,
    estimatedWaitTime,
    isPolling,
    forceRefresh
  } = useGoogleReviews({
    businessName,
    location,
    prospectId,
    domain,
    languageCode: 'fr',
    reviewsLimit: 3,
    autoStart: false // Disabled: analyses run during form submission
  });

  console.log('🔍 [GoogleReviewsBox] État actuel:', { taskStatus, hasBusinessInfo: !!businessInfo, prospectId });

  // Réduire le temps d'attente affiché pour une meilleure expérience utilisateur
  const displayWaitTime = React.useMemo(() => {
    // Si aucun temps d'attente ou si données déjà disponibles, ne pas afficher de temps
    if (estimatedWaitTime <= 0 || taskStatus === 'completed') return 0;
    // Limiter le temps d'attente maximum affiché à 10 minutes pour une meilleure UX
    return Math.min(estimatedWaitTime, 10);
  }, [estimatedWaitTime, taskStatus]);

  // Assure que la valeur est bien un nombre et arrondit à un nombre entier pour l'affichage des étoiles
  const renderStars = (ratingValue: number | string | { value: string | number } | null | undefined) => {
    // Convertir en nombre et s'assurer que c'est une valeur valide
    const rating = typeof ratingValue === 'number' ? ratingValue :
                 typeof ratingValue === 'string' ? parseFloat(ratingValue) :
                 typeof ratingValue === 'object' && ratingValue?.value ? parseFloat(String(ratingValue.value)) : 0;
    
    // Gérer les valeurs NaN
    const safeRating = isNaN(rating) ? 0 : rating;
    const roundedRating = Math.round(safeRating);
    
    return (
      <div className="flex items-center">
        {Array.from({ length: 5 }, (_, i) => (
          <motion.div 
            key={i} 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: i * 0.1, duration: 0.3 }}
            whileHover={{ scale: 1.2, rotate: 10 }}
            style={{ display: 'inline-block' }} 
          >
            <Star className={`w-6 h-6 ${i < roundedRating ? 'fill-current' : 'fill-gray-300'}`} 
                  style={{ color: i < roundedRating ? '#eb811c' : '#d1d5db' }} />
          </motion.div>
        ))}
      </div>
    );
  };
  
  // Fonction utilitaire pour obtenir un nombre sûr à partir de toute valeur


  const formatWaitTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h${remainingMinutes > 0 ? ` ${remainingMinutes}min` : ''}`;
  };

  const getStatusMessage = () => {
    switch (taskStatus) {
      case 'pending':
        return {
          icon: <Clock className="w-5 h-5" style={{ color: '#eb811c' }} />,
          message: 'Recherche des avis Google en cours...',
          subMessage: displayWaitTime > 0
            ? `Temps d'attente estimé : ${formatWaitTime(displayWaitTime)}`
            : 'Analyse en cours...'
        };
      case 'processing':
        return {
          icon: <RefreshCw className="w-5 h-5 animate-spin" style={{ color: '#eb811c' }} />,
          message: 'Traitement des avis Google...',
          subMessage: 'Les données sont en cours de récupération'
        };
      case 'failed':
        return {
          icon: <AlertCircle className="w-5 h-5" style={{ color: '#ef4444' }} />,
          message: 'Erreur lors de la récupération',
          subMessage: 'Impossible de récupérer les avis Google pour le moment'
        };
      case 'completed':
        return {
          icon: <CheckCircle className="w-5 h-5" style={{ color: '#10b981' }} />,
          message: 'Avis Google récupérés',
          subMessage: 'Données mises à jour avec succès'
        };
      default:
        return {
          icon: <Search className="w-5 h-5" style={{ color: '#eb811c' }} />,
          message: 'Initialisation...',
          subMessage: 'Préparation de la recherche'
        };
    }
  };

  // Si la tâche est complétée et qu'on a les données, afficher les avis
  if (taskStatus === 'completed' && businessInfo) {
    // Cas où il n'y a aucun avis - état élégant spécifique
    if (!businessInfo.reviews || businessInfo.reviews.length === 0) {
      return (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 transition-all duration-300 hover:shadow-md"
        >
          <div className="flex flex-col items-center justify-center space-y-4">
            <motion.div 
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
              className="h-16 w-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#fef3e2' }}
            >
              <Search className="w-8 h-8" style={{ color: '#eb811c' }} />
            </motion.div>
            
            <div className="text-center">
              <h3 className="text-lg font-semibold" style={{ color: '#0f0f0f' }}>Aucun avis Google trouvé</h3>
              <p className="text-sm mt-1" style={{ color: '#6b7280' }}>{businessName} n'a pas encore d'avis Google ou nous n'avons pas pu les récupérer.</p>
            </div>
            
            <div className="w-full sm:w-4/5">
              <motion.div 
                className="w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <div className="text-center text-sm font-medium bg-amber-50 p-3 rounded-md border border-amber-200">
                  <p className="text-amber-800">
                    C'est le moment idéal pour commencer à collecter des avis et prendre de l'avance sur vos concurrents !
                  </p>
                </div>
              </motion.div>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full sm:w-auto bg-orange-500 text-white font-medium py-2 px-6 rounded-md shadow-md hover:shadow-lg transition-all duration-300"
            >
              Commencer à collecter des avis
            </motion.button>
          </div>
        </motion.div>
      );
    }
    
    // Cas où il y a des avis - affichage normal amélioré avec responsivité
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 transition-all duration-300 hover:shadow-md"
      >
        <div className="flex flex-col space-y-3 sm:space-y-4 flex-grow">
          {/* En-tête avec logo Google et infos - amélioré pour la responsivité */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
            <div className="flex items-center space-x-2">
              <div>
                <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                  {businessName}
                </h3>
                {location && <p className="text-gray-600 text-xs sm:text-sm">{location}</p>}
              </div>
            </div>
            
            <motion.div 
              className="flex items-center gap-1 bg-orange-500 text-white px-3 py-1 rounded-full self-start sm:self-auto"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Award className="w-4 h-4" />
              <span className="font-bold text-lg">{getSafeNumber(businessInfo.rating).toFixed(1)}</span>
              <span className="text-xs">/5</span>
            </motion.div>
          </div>
          
          {/* Étoiles animées et statistiques - amélioré pour la responsivité */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between bg-white p-3 rounded-lg shadow-sm gap-3 sm:gap-0">
            <div className="flex-1">
              {renderStars(businessInfo.rating)}
            </div>
            
            <div className="flex items-center space-x-2 w-full sm:w-auto justify-start sm:justify-end">
              <motion.div 
                className={`flex items-center space-x-1 ${
                  (businessInfo.reviews_count || 0) < 10 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-green-100 text-green-800'
                } px-2 py-1 rounded-md`}
                whileHover={{ scale: 1.05 }}
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <ThumbsUp className={`w-4 h-4 ${(businessInfo.reviews_count || 0) < 10 ? 'transform rotate-180' : ''}`} />
                <span className="font-medium">{businessInfo.reviews_count || 0} avis</span>
              </motion.div>
              
              <motion.div 
                className={`flex items-center space-x-1 ${
                  (businessInfo.reviews_count || 0) < 10 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-green-100 text-green-800'
                } px-2 py-1 rounded-md`}
                whileHover={{ scale: 1.05 }}
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <TrendingUp className={`w-4 h-4 ${(businessInfo.reviews_count || 0) >= 10 ? '' : 'transform rotate-180'}`} />
                <span className="font-medium">
                  {/* Calculer un pourcentage basé sur le nombre d'avis et la note */}
                  {Math.round((businessInfo.reviews_count || 0) > 0 ? 
                    Math.min(100, Math.max(10, (businessInfo.reviews_count || 0) * 5 + (getSafeNumber(businessInfo.rating) * 5))) : 38)}%
                </span>
              </motion.div>
            </div>
          </div>
          
          {/* Message informatif avec animation */}
          <motion.div 
            className="w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 0.4 }}
          >
            {(businessInfo.reviews_count || 0) >= 50 ? (
              <div className="text-center text-xs sm:text-sm font-medium bg-green-100 p-2 sm:p-3 rounded-md border-l-4 border-green-600 shadow-inner">
                <span className="text-green-800">Excellente réputation en ligne !</span> Avec <span className="text-green-600 font-bold">{businessInfo.reviews_count} avis</span>, vous avez une base solide de confiance client.<br />
                <span className="text-green-700">Continuez à encourager vos clients satisfaits</span> à partager leur expérience pour maintenir cette excellente dynamique !
              </div>
            ) : (businessInfo.reviews_count || 0) >= 10 ? (
              <div className="text-center text-xs sm:text-sm font-medium bg-green-50 p-2 sm:p-3 rounded-md border-l-4 border-green-600 shadow-inner">
                <span className="text-green-800">Bonne réputation en ligne !</span> Avec <span className="text-green-600 font-bold">{businessInfo.reviews_count} avis</span> et une note de <span className="text-green-600 font-bold">{getSafeNumber(businessInfo.rating).toFixed(1)}/5</span>, vous avez une base solide de confiance client.<br />
                <span className="text-green-700">Continuez à encourager vos clients satisfaits</span> à partager leur expérience pour renforcer cette dynamique !
              </div>
            ) : (businessInfo.reviews_count || 0) > 0 ? (
              <div className="text-center text-xs sm:text-sm font-medium bg-orange-50 p-2 sm:p-3 rounded-md border-l-4 border-orange-600 shadow-inner">
                <span className="text-orange-800">Début de réputation en ligne.</span> Avec <span className="text-orange-600 font-bold">{businessInfo.reviews_count} avis</span>, vous commencez à établir votre présence en ligne.<br />
                <span className="text-orange-700">Encouragez activement vos clients satisfaits</span> à partager leur expérience pour développer votre réputation !
              </div>
            ) : (
              <div className="text-center text-xs sm:text-sm font-medium bg-orange-50 p-2 sm:p-3 rounded-md border-l-4 border-orange-600 shadow-inner">
                Les entreprises qui sollicitent régulièrement des avis clients constatent jusqu'à <span className="text-orange-600 font-bold">2x plus de contacts</span> et une meilleure confiance de la part de leurs prospects.
              </div>
            )}
          </motion.div>
          
          {/* Métriques additionnelles */}
          <div className="mt-2 pt-3 border-t grid grid-cols-2 gap-3 text-sm">
            <motion.div 
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="flex flex-col items-center p-2 bg-orange-50 rounded-lg"
            >
              <span className="text-gray-500 mb-1 flex items-center">
                <ThumbsUp className="w-3 h-3 mr-1 text-orange-500" /> Avis positifs
              </span>
              <motion.span 
                whileHover={{ scale: 1.1 }}
                className="font-medium text-orange-700"
              >
                {Math.round(getSafeNumber(businessInfo.rating) * 20)}%
              </motion.span>
            </motion.div>
            
            <motion.div 
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="flex flex-col items-center p-2 bg-orange-50 rounded-lg"
            >
              <span className="text-gray-500 mb-1 flex items-center">
                <TrendingUp className="w-3 h-3 mr-1 text-orange-500" /> Fréquence
              </span>
              <motion.span 
                whileHover={{ scale: 1.1 }}
                className="font-medium text-orange-700"
              >
                {(businessInfo.reviews_count || 0) > 50 ? 'Régulière' : 'Faible'}
              </motion.span>
            </motion.div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Afficher un état de chargement attractif ou un état d'erreur
  const statusInfo = getStatusMessage();

  // État spécifique pour l'erreur de recherche sans avis
  if (taskStatus === 'failed' && error && error.toLowerCase().includes('aucun avis')) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 transition-all duration-300 hover:shadow-md"
      >
        <div className="flex flex-col items-center justify-center space-y-4">
          <motion.div 
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
            className="h-16 w-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#fef3e2' }}
          >
            <Search className="w-8 h-8" style={{ color: '#eb811c' }} />
          </motion.div>
          
          <div className="text-center">
            <h3 className="text-lg font-semibold" style={{ color: '#0f0f0f' }}>Recherche sans résultat</h3>
            <p className="text-sm mt-1" style={{ color: '#6b7280' }}>Nous n'avons pas trouvé d'avis Google pour cette entreprise</p>
          </div>
          
          <motion.div 
            className="w-full sm:w-4/5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="text-center text-sm font-medium bg-amber-50 p-3 rounded-md border border-amber-200">
              <p className="text-amber-800">
                C'est le moment idéal pour commencer à collecter des avis et prendre de l'avance sur vos concurrents !
              </p>
            </div>
          </motion.div>
          
          <div className="flex space-x-3 w-full sm:w-auto">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                forceRefresh();
              }}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-md shadow-sm hover:bg-gray-300 transition-colors text-sm"
            >
              Rafraîchir
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-md shadow-sm hover:bg-orange-600 transition-colors text-sm"
            >
              Solution d'avis
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }
  
  // État de chargement par défaut - amélioration de la responsivité
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 transition-all duration-300 hover:shadow-md"
    >
      <div className="flex flex-col items-center justify-center space-y-4">
        <motion.div 
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="h-16 w-16 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#fef3e2' }}
        >
          {statusInfo.icon}
        </motion.div>
        
        <div className="text-center">
          <h3 className="text-lg font-semibold" style={{ color: '#0f0f0f' }}>{statusInfo.message}</h3>
          <p className="text-sm mt-1" style={{ color: '#6b7280' }}>{statusInfo.subMessage}</p>
        </div>
        
        {taskStatus === 'failed' && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              forceRefresh();
            }}
            className="mt-2 px-4 py-2 rounded-md shadow-sm transition-colors text-sm text-white"
            style={{ backgroundColor: '#eb811c' }}
          >
            Réessayer
          </motion.button>
        )}
        
        <div className="w-full mt-2">
          <div className="overflow-hidden h-1 bg-gray-200 rounded-full">
            <motion.div 
              className="h-full rounded-full"
              style={{ backgroundColor: '#eb811c' }}
              initial={{ width: '0%' }}
              animate={{ width: isPolling ? '90%' : '100%' }}
              transition={{ duration: estimatedWaitTime > 0 ? estimatedWaitTime / 60 : 3 }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default GoogleReviewsBox;
